import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { setEmonUsdRate, getEmonUsdRate, EMON_DECIMALS } from '@/integrations/anchor/emon-nft-bonding-idl';
import { supabase } from '@/integrations/supabase/client';

// Fallback rate: ~0.002 USD per EMON (realistic rate)
// This gives: $1 USD / 0.002 = ~500 EMON
const FALLBACK_RATE = 0.002;

// Refresh interval (60 seconds - reads from cache, not API)
const REFRESH_INTERVAL_MS = 60000;

interface EmonPriceState {
  rate: number;
  lastUpdated: Date | null;
  isLoading: boolean;
  error: string | null;
  source: 'database' | 'fallback' | 'cached';
  dexSource: string | null; // Jupiter, DexScreener, fallback, etc.
}

interface EmonPriceContextValue extends EmonPriceState {
  refresh: () => Promise<number>;
  formattedRate: string;
  timeSinceUpdate: number | null;
  usdToEmon: (usdAmount: number) => number;
  emonToUsd: (emonAmount: number) => number;
  triggerPriceUpdate: () => Promise<void>;
}

const EmonPriceContext = createContext<EmonPriceContextValue | null>(null);

// Event emitter for price updates (allows non-React code to subscribe)
type PriceListener = (rate: number) => void;
const priceListeners: Set<PriceListener> = new Set();

export const subscribeToPriceUpdates = (listener: PriceListener) => {
  priceListeners.add(listener);
  return () => priceListeners.delete(listener);
};

const notifyPriceListeners = (rate: number) => {
  priceListeners.forEach(listener => listener(rate));
};

export function EmonPriceProvider({ children }: { children: ReactNode }) {
  const [priceState, setPriceState] = useState<EmonPriceState>({
    rate: getEmonUsdRate(),
    lastUpdated: null,
    isLoading: true,
    error: null,
    source: 'cached',
    dexSource: null
  });

  // Fetch price from database cache
  const fetchPrice = useCallback(async (): Promise<number> => {
    setPriceState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      // Read from database cache (populated by edge function)
      const { data, error } = await supabase
        .from('emon_price_cache')
        .select('emon_usd_rate, updated_at, dex_source')
        .eq('id', 'current')
        .single();

      if (error) {
        throw new Error(`Database error: ${error.message}`);
      }

      if (data?.emon_usd_rate && data.emon_usd_rate > 0) {
        const rate = Number(data.emon_usd_rate);
        
        // Update global rate
        setEmonUsdRate(rate);
        
        // Notify all listeners
        notifyPriceListeners(rate);
        
        setPriceState({
          rate,
          lastUpdated: data.updated_at ? new Date(data.updated_at) : new Date(),
          isLoading: false,
          error: null,
          source: 'database',
          dexSource: data.dex_source || null
        });
        
        console.log(`[EmonPriceContext] Rate from DB: $${rate.toFixed(8)} (source: ${data.dex_source})`);
        return rate;
      } else {
        throw new Error('No valid price in cache');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.warn(`[EmonPriceContext] Cache read failed: ${errorMessage}, using fallback`);
      
      setEmonUsdRate(FALLBACK_RATE);
      notifyPriceListeners(FALLBACK_RATE);
      
      setPriceState({
        rate: FALLBACK_RATE,
        lastUpdated: new Date(),
        isLoading: false,
        error: errorMessage,
        source: 'fallback',
        dexSource: 'fallback'
      });
      
      return FALLBACK_RATE;
    }
  }, []);

  // Trigger edge function to update price cache
  const triggerPriceUpdate = useCallback(async () => {
    try {
      console.log('[EmonPriceContext] Triggering price update via edge function...');
      const { data, error } = await supabase.functions.invoke('fetch-emon-price');
      
      if (error) {
        console.error('[EmonPriceContext] Edge function error:', error);
      } else {
        console.log('[EmonPriceContext] Edge function response:', data);
        // Refresh from database after edge function updates
        await fetchPrice();
      }
    } catch (err) {
      console.error('[EmonPriceContext] Failed to trigger price update:', err);
    }
  }, [fetchPrice]);

  // Initial fetch
  useEffect(() => {
    fetchPrice();
  }, [fetchPrice]);

  // Auto-refresh with interval
  useEffect(() => {
    const interval = setInterval(fetchPrice, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchPrice]);

  // Conversion utilities (returns human-readable tokens, not smallest units)
  const usdToEmon = useCallback((usdAmount: number): number => {
    if (priceState.rate <= 0) return 0;
    return usdAmount / priceState.rate;
  }, [priceState.rate]);

  const emonToUsd = useCallback((emonAmount: number): number => {
    return emonAmount * priceState.rate;
  }, [priceState.rate]);

  // Conversion to smallest units for transfers (includes decimals)
  const usdToEmonSmallestUnits = useCallback((usdAmount: number): number => {
    if (priceState.rate <= 0) return 0;
    const emonTokens = usdAmount / priceState.rate;
    return Math.floor(emonTokens * Math.pow(10, EMON_DECIMALS));
  }, [priceState.rate]);

  const contextValue: EmonPriceContextValue = {
    ...priceState,
    refresh: fetchPrice,
    formattedRate: `$${priceState.rate.toFixed(6)}`,
    timeSinceUpdate: priceState.lastUpdated 
      ? Math.floor((Date.now() - priceState.lastUpdated.getTime()) / 1000)
      : null,
    usdToEmon,
    emonToUsd,
    triggerPriceUpdate
  };

  return (
    <EmonPriceContext.Provider value={contextValue}>
      {children}
    </EmonPriceContext.Provider>
  );
}

// Main hook for consuming EMON price
export function useEmonPriceContext() {
  const context = useContext(EmonPriceContext);
  if (!context) {
    throw new Error('useEmonPriceContext must be used within EmonPriceProvider');
  }
  return context;
}

// Simplified hook that just returns the rate and loading state
export function useEmonRate() {
  const { rate, isLoading, usdToEmon, emonToUsd } = useEmonPriceContext();
  return { rate, isLoading, usdToEmon, emonToUsd };
}
